import { useState, useEffect, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Brand,
  Button,
  Container,
  Divider,
  Form,
  FormField,
  Group,
  Icon,
  IconKey,
  Link,
  Loader,
  SectionBannerCloseButton,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  SectionBannerTitle,
  SplitLayoutBody,
  SplitLayoutPanel,
  SplitLayoutPanelBody,
  SplitLayoutPanelFooter,
  SplitLayoutPanelHeader,
  SplitLayoutRoot,
  Text,
  TextField,
  useBreakpointValue,
  useMelioForm,
} from '@melio/penny';
import { object, string } from 'yup';

import { signUp, login } from '../../api/auth';
import { useAnalytics, type Cta, type ErrorType } from '../../analytics';
import { useVariant } from '../../contexts/VariantContext';
import { getEntryPoint, setEntryPoint, setIdentificationMethod, type IdentificationMethod } from '../../session';
import { useMfaNavigation } from '../../hooks/useMfaNavigation';
import { useStoreRedirectParam, completeAuthRedirect } from '../../hooks/useRedirectParam';
import { identifyUser } from '../../monitoring';
import { navigateToGoogleSSO, navigateToXeroSSO, navigateToIntuitSSO } from '../../hooks/useSsoNavigation';
import { TermsFooter } from '..';
import { PasswordField, PASSWORD_RULES } from '../PasswordField/PasswordField';
import { SsoButtons } from '../SsoButtons/SsoButtons';
import splitscreenImage from '../../images/splitscreen.png';

interface SignUpFormModel {
  email: string;
  password: string;
}

const useSignUpValidationSchema = () => {
  return object().shape({
    email: string().email('Please enter a valid email address').required('Email is required'),
    password: string().defined(),
  });
};

const isPasswordValid = (password: string): boolean => {
  return (
    PASSWORD_RULES.length.test(password) && PASSWORD_RULES.lettersAndNumber.test(password) && PASSWORD_RULES.specialChar.test(password)
  );
};

export const SignUpPage = () => {
  const navigate = useNavigate();
  const { navigateToMfaChallenge } = useMfaNavigation();
  const isMobile = useBreakpointValue({ xs: true, s: false });
  const isNarrowScreen = useBreakpointValue({ xs: true, l: false });
  const { track } = useAnalytics();
  const location = useLocation();
  const { variant, variantConfig, isLoading: isVariantLoading } = useVariant();
  useStoreRedirectParam();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(() => {
    const img = new Image();
    img.src = splitscreenImage;
    return img.complete;
  });
  const handleImageLoad = useCallback(() => setIsImageLoaded(true), []);
  const [error, setError] = useState<string | null>(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'sso_error') return 'Sign-up failed. Please try again or use a different sign-up method.';
    if (errorParam === 'sso_email_not_verified')
      return "Your account email isn't verified with the provider. Please verify it and try again.";
    return null;
  });

  useEffect(() => {
    if (searchParams.get('error') === 'sso_error' || searchParams.get('error') === 'sso_email_not_verified') {
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const schema = useSignUpValidationSchema();

  // Set entry point on mount
  setEntryPoint('sign-up');
  document.title = 'Create your Melio account';

  // Track page view
  useEffect(() => {
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: 'create-your-account',
        Flow: 'sign-up',
        Intent: 'sign-up',
        Variant: variant,
        EntryPoint: getEntryPoint(),
      },
    });
  }, [track, variant]);

  const trackClick = (cta: Cta, identificationMethod: IdentificationMethod = 'manually') => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'create-your-account',
        Flow: 'sign-up',
        Intent: 'sign-up',
        Variant: variant,
        EntryPoint: getEntryPoint(),
        Cta: cta,
        IdentificationMethod: identificationMethod,
        EmailInteraction: !!formState.touchedFields.email,
        PasswordInteraction: !!formState.touchedFields.password,
      },
    });
  };

  const trackStatus = (
    statusType: 'success' | 'failure',
    cta: Cta,
    identificationMethod: IdentificationMethod = 'manually',
    errorType?: ErrorType
  ) => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: 'create-your-account',
        Flow: 'sign-up',
        Intent: 'sign-up',
        Variant: variant,
        EntryPoint: getEntryPoint(),
        Cta: cta,
        IdentificationMethod: identificationMethod,
        StatusType: statusType,
        ...(errorType && { ErrorType: errorType }),
      },
    });
  };

  const {
    formProps,
    registerField,
    handleSubmit,
    formState,
    setError: setFieldError,
  } = useMelioForm<SignUpFormModel>({
    onSubmit: async (data) => {
      if (!isPasswordValid(data.password)) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setIdentificationMethod('manually');
      trackClick('continue', 'manually');

      const signUpResult = await signUp({
        email: data.email,
        password: data.password,
      });

      switch (signUpResult.type) {
        case 'invalidPassword':
          trackStatus('failure', 'continue', 'manually', 'invalid-password');
          setError(signUpResult.message);
          setIsLoading(false);
          return;

        case 'invalidEmail':
          trackStatus('failure', 'continue', 'manually', 'invalid-email');
          setFieldError('email', { message: signUpResult.message });
          setIsLoading(false);
          return;

        case 'error':
          trackStatus('failure', 'continue', 'manually');
          setError(signUpResult.message);
          setIsLoading(false);
          return;

        case 'userAlreadyValidated': {
          // User exists and is validated - proceed with login flow
          const loginResult = await login({ email: data.email, password: data.password });

          switch (loginResult.type) {
            case 'invalidCredentials':
            case 'accountBlocked':
            case 'tooManyAttempts':
            case 'error':
              trackStatus('failure', 'continue', 'manually', 'login-info-incorrect');
              setError(loginResult.message);
              setIsLoading(false);
              return;

            case 'mfaRequired':
              trackStatus('success', 'continue', 'manually');
              navigateToMfaChallenge({
                mfaToken: loginResult.mfaToken,
                authenticators: loginResult.authenticators,
                challengedAuthenticators: loginResult.challengedAuthenticators,
              });
              setIsLoading(false);
              return;

            case 'success':
              identifyUser(loginResult.user);
              trackStatus('success', 'continue', 'manually');
              completeAuthRedirect();
              setIsLoading(false);
              return;

            default: {
              const _exhaustiveCheck: never = loginResult;
              return _exhaustiveCheck;
            }
          }
        }

        case 'mfaRequired':
          trackStatus('success', 'continue', 'manually');
          // New user - navigate to MFA for email verification
          navigateToMfaChallenge({
            mfaToken: signUpResult.mfaToken,
            authenticators: signUpResult.authenticators,
            challengedAuthenticators: signUpResult.challengedAuthenticators,
          });
          setIsLoading(false);
          return;

        default: {
          const _exhaustiveCheck: never = signUpResult;
          return _exhaustiveCheck;
        }
      }
    },
    schema,
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleGoogleSignUp = () => {
    setIdentificationMethod('google');
    trackClick('sign-in-with-google', 'google');
    navigateToGoogleSSO();
  };

  const handleXeroSignUp = () => {
    setIdentificationMethod('xero');
    trackClick('sign-in-with-xero', 'xero');
    navigateToXeroSSO();
  };

  const handleIntuitSignUp = () => {
    setIdentificationMethod('intuit');
    trackClick('sign-in-with-intuit', 'intuit');
    navigateToIntuitSSO();
  };

  const handleSignIn = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    trackClick('sign-in', 'manually');
    navigate({ pathname: '/sign-in', search: location.search });
  };

  const onSubmit = handleSubmit();

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setHasAttemptedSubmit(true);
      onSubmit();
    }
  };

  const handleCreateAccount = () => {
    setHasAttemptedSubmit(true);
    onSubmit();
  };

  const leftPanelContent = (
    <Container height="full" paddingX={isMobile ? 's' : 'xl'} paddingY="s" alignItems="center" justifyContent="center" overflow="visible">
      <Container overflow="visible" width="full" maxWidth="432px">
        <Group variant="vertical" alignItems="stretch" spacing="m">
          {/* Error Banner - at the top */}
          {error && (
            <SectionBannerRoot variant="critical" data-testid="signup-error-banner">
              <SectionBannerIcon />
              <SectionBannerContent>
                <SectionBannerDescription>{error}</SectionBannerDescription>
              </SectionBannerContent>
              <SectionBannerCloseButton onClick={() => setError(null)} />
            </SectionBannerRoot>
          )}

          {/* Header + SSO Buttons with 40px gap between "Already have an account" and SSO */}
          <Group variant="vertical" spacing="none">
            <Group variant="vertical" spacing={isMobile ? 'xxs' : 'xs'}>
              <Text textStyle={isMobile ? 'heading2Semi' : 'heading1Semi'} color="semantic.text.primary" data-testid="signup-page-title">
                {variantConfig?.h1 || 'Create your account'}
              </Text>

              {/* Sign In Link - positioned below title */}
              <Group variant="horizontal" spacing="xs" alignItems="center">
                <Text textStyle="body2" color="semantic.text.primary">
                  Already have an account?
                </Text>
                <Link
                  href={`/sign-in${location.search}`}
                  color="secondary"
                  variant="standalone"
                  size="large"
                  label="Sign in"
                  onClick={handleSignIn}
                  data-testid="sign-in-link"
                />
              </Group>
            </Group>

            {variantConfig?.banner && (
              <Container style={{ marginTop: 16 }}>
                <SectionBannerRoot variant="brand" data-testid="variant-banner">
                  <Icon type={variantConfig.banner.icon as IconKey} color="brand" />
                  <SectionBannerContent>
                    {variantConfig.banner.title && <SectionBannerTitle>{variantConfig.banner.title}</SectionBannerTitle>}
                    <SectionBannerDescription>{variantConfig.banner.description}</SectionBannerDescription>
                  </SectionBannerContent>
                </SectionBannerRoot>
              </Container>
            )}

            <Container style={{ marginTop: 40 }}>
              <SsoButtons
                variant="sign-up"
                onGoogleClick={handleGoogleSignUp}
                onXeroClick={handleXeroSignUp}
                onIntuitClick={handleIntuitSignUp}
              />
            </Container>
          </Group>

          <Divider label="or" />

          {/* Email/Password Form */}
          <Form {...formProps} rowGap="s" onKeyDown={handleFormKeyDown} columns={1} autoComplete="on" data-testid="signup-form">
            <FormField
              {...registerField('email')}
              labelProps={{ label: 'Email address' }}
              isRequired
              size={isMobile ? 'small' : 'large'}
              render={(fieldProps) => <TextField {...fieldProps} placeholder="" autoComplete="email" data-testid="email-input" />}
            />

            <PasswordField
              label="Password"
              fieldProps={registerField('password')}
              testId="password-input"
              showInvalidIcon={hasAttemptedSubmit}
              autoComplete="new-password"
              size={isMobile ? 'small' : 'large'}
            />

            <FormField
              render={() => (
                <Container paddingTop="xs">
                  <Button
                    label={variantConfig?.cta || 'Create account'}
                    variant="primary"
                    size={isMobile ? 'medium' : 'large'}
                    isFullWidth
                    isLoading={isLoading}
                    onClick={handleCreateAccount}
                    data-testid="continue-button"
                  />
                </Container>
              )}
            />
          </Form>
        </Group>
      </Container>
    </Container>
  );

  const leftPanelHeader = (
    <Group variant="vertical" spacing="none" alignItems="stretch">
      <Container paddingX={isMobile ? 's' : 'l'} paddingY="s">
        <Container
          alignItems="center"
          style={isMobile ? undefined : { display: 'inline-block', transform: 'scale(1.5)', transformOrigin: 'left top' }}
        >
          <Brand type="melio" data-testid="melio-brand" />
        </Container>
      </Container>
      {isMobile ? <Divider /> : null}
    </Group>
  );

  const leftPanelFooter = (
    <Container justifyContent="center" paddingX={isMobile ? 's' : 'l'} paddingBottom={isMobile ? 's' : 'l'}>
      <Container width="full">
        <TermsFooter />
      </Container>
    </Container>
  );

  const rightPanelContent = (
    <Container height="full" backgroundColor="light" alignItems="center" justifyContent="center" paddingX="xl" paddingY="xl">
      <Group variant="vertical" spacing="xxl" alignItems="center">
        <Container maxWidth="494px" width="full" alignItems="center" justifyContent="center">
          <img
            src={splitscreenImage}
            alt="QuickBooks and Xero integration"
            style={{
              width: '494px',
              height: '468px',
              flexShrink: 0,
            }}
          />
        </Container>

        <Container maxWidth="416px" width="full" paddingX="l">
          <Group variant="vertical" spacing="xs" alignItems="stretch">
            <Text textStyle="body2Semi" color="semantic.text.secondary">
              Have QuickBooks or Xero?
            </Text>
            <Text textStyle="body2" color="semantic.text.secondary">
              Sign in with your Intuit or Xero account–we&apos;ll 2-way sync your bills, invoices, payments and contacts automatically.
            </Text>
          </Group>
        </Container>
      </Group>
    </Container>
  );

  if (isVariantLoading || !isImageLoaded) {
    return (
      <Container height="full" data-testid="signup-page" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <Loader />
        <img src={splitscreenImage} alt="" onLoad={handleImageLoad} onError={handleImageLoad} style={{ display: 'none' }} />
      </Container>
    );
  }

  if (isNarrowScreen || variantConfig?.disableSplitScreen) {
    return (
      <Container height="full" data-testid="signup-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {leftPanelHeader}
        <Container style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>{leftPanelContent}</Container>
        {leftPanelFooter}
      </Container>
    );
  }

  return (
    <Box minHeight="100vh" overflow="auto" display="grid">
      <SplitLayoutRoot data-testid="signup-page">
        <SplitLayoutBody>
          <SplitLayoutPanel size={50}>
            <SplitLayoutPanelHeader>{leftPanelHeader}</SplitLayoutPanelHeader>
            <SplitLayoutPanelBody>{leftPanelContent}</SplitLayoutPanelBody>
            <SplitLayoutPanelFooter>{leftPanelFooter}</SplitLayoutPanelFooter>
          </SplitLayoutPanel>
          <SplitLayoutPanel size={50}>{rightPanelContent}</SplitLayoutPanel>
        </SplitLayoutBody>
      </SplitLayoutRoot>
    </Box>
  );
};
